import { PlusIcon } from '@radix-ui/react-icons'
import { useMatchRoute, useNavigate } from '@tanstack/react-router'
import { Button } from 'antd'
import { PermissionsGate } from 'features/permissions'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoOrganization,
  GeckoSite,
  PERMISSION_CANTILEVER_CORE_ADMIN,
} from 'types'
import { groupBy } from 'utils/fde/maps'

import DropdownSearch from './DropdownSearch'

const SiteSelectorsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.125rem;
`

const SelectorWrapper = styled.div`
  max-width: clamp(10rem, 15rem, 20rem);
  min-width: 8rem;
  overflow: hidden;
  flex: 0 1 auto;
`

const StyledLinkDiv = styled.div`
  display: flex;
  justify-content: space-between;
`

const StyledButtonDiv = styled.div`
  display: flex;
  justify-content: end;
`

const StyledButton = styled(Button)`
  cursor: pointer;
  font-weight: bold;
  background-color: ${({ theme }) => theme.forecast.defaultButton} !important;

  &:hover {
    background-color: ${({ theme }) =>
      theme.forecast.hoverGhostButton} !important;
    color: ${({ theme }) => theme.forecast.activeText} !important;
  }

  &:active {
    background-color: ${({ theme }) =>
      theme.forecast.activeGhostButton} !important;
  }
`

export interface SiteSelectorsProps {
  selectedOrganization: GeckoOrganization | undefined
  organizations: ReadonlyArray<GeckoOrganization>
  selectedSite: GeckoSite | undefined
  sites: ReadonlyArray<GeckoSite>
  isLoading: boolean
  hasEverLoaded: boolean
  hideSiteSelector?: boolean
}

export const SiteSelectors: React.FC<SiteSelectorsProps> = ({
  organizations,
  selectedOrganization,
  sites,
  selectedSite,
  isLoading,
  hasEverLoaded,
  hideSiteSelector = false,
}) => {
  const navigate = useNavigate()
  const matchRoute = useMatchRoute()

  const sitesByOrgId = useMemo(() => {
    return groupBy({
      arr: sites,
      key: 'tags.organization',
    })
  }, [sites])

  const orgsWithAssociatedSites = useMemo(() => {
    return organizations.toSorted(GeckoOrganization.DefaultSort).map((o) => ({
      organization: o,
      sites: sitesByOrgId.get(o.id) ?? [],
    }))
  }, [organizations, sitesByOrgId])

  const sitesFilteredByOrganization = useMemo(
    () =>
      selectedOrganization
        ? (sitesByOrgId.get(selectedOrganization.id) ?? []).toSorted(
            GeckoSite.DefaultSort
          )
        : [],
    [selectedOrganization, sitesByOrgId]
  )

  const handleSiteSelect = (site: GeckoSite | undefined) => {
    if (!site) return

    if (
      matchRoute({ to: '/assets/$siteID/$unitID' }) ||
      matchRoute({ to: '/assets/{-$siteID}' })
    ) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/details/$entryID' })) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (
      matchRoute({ to: '/health-check' }) ||
      matchRoute({ to: '/admin/users' })
    ) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/viewer/$unitID/configuration' })) {
      navigate({
        to: `/assets/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns/$siteID/$walkdownId' })) {
      navigate({
        to: `/walkdowns/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/walkdowns/$siteID' })) {
      navigate({
        to: `/walkdowns/${site.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/explorer/$orgOrSiteID' })) {
      navigate({
        to: `/explorer/${site.id}`,
      })
    } else if (matchRoute({ to: '/inbox/$orgOrSiteID' })) {
      navigate({
        to: `/inbox/${site.id}`,
      })
    } else if (matchRoute({ to: '/inbox' })) {
      navigate({
        to: `/inbox/${site.id}`,
      })
    } else if (matchRoute({ to: '/explorer' })) {
      navigate({
        to: `/explorer/${site.id}`,
      })
    } else {
      navigate({
        to: '.',
        params: { siteID: site.id },
      })
    }
  }

  const handleOrgSelect = (
    item:
      | {
          organization: GeckoOrganization
          sites: GeckoSite[]
        }
      | undefined
  ) => {
    const site = item?.sites?.at(0)
    const organization = item?.organization

    if (matchRoute({ to: '/users/$orgID' })) {
      navigate({
        to: `/users/${organization?.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/extensions/$orgID' })) {
      navigate({
        to: `/extensions/${organization?.id}`,
        replace: true,
      })
    } else if (
      matchRoute({ to: '/libra/$orgID' }) ||
      matchRoute({ to: '/libra/$orgID/$workflowId' })
    ) {
      navigate({
        to: `/libra/${organization?.id}`,
        replace: true,
      })
    } else if (matchRoute({ to: '/explorer/$orgOrSiteID' })) {
      navigate({
        to: `/explorer/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/inbox/$orgOrSiteID' })) {
      navigate({
        to: `/inbox/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/inbox' })) {
      navigate({
        to: `/inbox/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/explorer' })) {
      navigate({
        to: `/explorer/${organization?.id}`,
      })
    } else if (matchRoute({ to: '/production-dashboard/$orgID/$siteID' })) {
      navigate({
        to: `/production-dashboard/${organization?.id}/${site ? site.id : ''}`,
      })
    } else if (site) {
      handleSiteSelect(site)
    } else if (!site && organization?.id) {
      navigate({
        to: '/organization/$orgID',
        params: { orgID: organization?.id },
        search: { mode: 'edit' },
        replace: true,
      })
    } else {
      navigate({
        to: '.',
        params: { orgID: organization?.id },
        replace: true,
      })
    }
  }

  const { setDrawerState, setDrawerEntry } = useRightDrawer()

  const getEditLink = useCallback(
    (
      classToEdit: GeckoOrganization | GeckoSite,
      closeMenu: () => Promise<void>
    ) => {
      let linkTitle

      const handleEditClick = async (e: React.MouseEvent) => {
        await closeMenu()
        setDrawerEntry(classToEdit)

        if (classToEdit instanceof GeckoOrganization) {
          navigate({
            to: '/organization/$orgID',
            search: { mode: 'edit' },
            params: { orgID: classToEdit.id },
          })
        } else if (classToEdit instanceof GeckoSite) {
          navigate({
            to: '/organization/$orgID/site/$siteID',
            search: { mode: 'edit' },
            params: {
              orgID: classToEdit.tags.organization,
              siteID: classToEdit.id,
            },
          })
        } else {
          setDrawerState(DrawerState.ErrorState)
        }

        e.preventDefault()
      }

      if (classToEdit instanceof GeckoOrganization) {
        linkTitle = 'Edit Org'
      } else if (classToEdit instanceof GeckoSite) {
        linkTitle = 'Edit Site'
      } else {
        linkTitle = 'Edit'
      }

      return (
        <div onClick={handleEditClick}>
          <a data-edit-link='true'>{linkTitle}</a>
        </div>
      )
    },
    [setDrawerEntry, setDrawerState, navigate]
  )

  const getAddButton = useCallback(
    (classToAdd: DrawerState, closeMenu: () => Promise<void>) => {
      let title
      if (classToAdd === DrawerState.EditGeckoOrganization) {
        title = 'Add Org'
        return (
          <StyledButtonDiv>
            <StyledButton
              onClick={async (e) => {
                await closeMenu()
                navigate({
                  to: '/organization',
                  search: { mode: 'create' },
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </StyledButton>
          </StyledButtonDiv>
        )
      } else if (classToAdd === DrawerState.EditGeckoSite) {
        title = 'Add Site'
        return (
          <StyledButtonDiv>
            <StyledButton
              onClick={async (e) => {
                await closeMenu()
                navigate({
                  to: '/organization/$orgID/site',
                  params: { orgID: selectedOrganization?.id ?? '' },
                  search: { mode: 'create' },
                })
                e.preventDefault()
              }}>
              <PlusIcon />
              {title}
            </StyledButton>
          </StyledButtonDiv>
        )
      } else {
        throw new Error(
          `Unexpected getAddButton state! ${DrawerState[classToAdd]}`
        )
      }
    },
    [selectedOrganization, navigate]
  )

  return (
    <SiteSelectorsContainer>
      <SelectorWrapper>
        <DropdownSearch>
          <DropdownSearch.Trigger
            contentData={selectedOrganization}
            isSingleItem={orgsWithAssociatedSites.length === 1}
            isLoading={(isLoading || !hasEverLoaded) && !selectedOrganization}
            testId='org-dropdown'
          />
          <DropdownSearch.Menu>
            <DropdownSearch.FilteredSearch
              listToFilter={orgsWithAssociatedSites}
              itemGetter={({ organization }, closeMenu) => (
                <StyledLinkDiv>
                  {organization.data.name}
                  <PermissionsGate
                    requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                    {getEditLink(organization, closeMenu)}
                  </PermissionsGate>
                </StyledLinkDiv>
              )}
              itemSearchTerms={({ organization }) => [
                organization.data.name,
                organization.data.name.replaceAll('.', ''),
                organization.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
              ]}
              onSelect={handleOrgSelect}
              placeholder='Search Organizations(…)'
              extra={({ closeMenu }) => (
                <PermissionsGate requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                  {getAddButton(DrawerState.EditGeckoOrganization, closeMenu)}
                </PermissionsGate>
              )}
            />
          </DropdownSearch.Menu>
        </DropdownSearch>
      </SelectorWrapper>

      {!hideSiteSelector && (
        <SelectorWrapper>
          <DropdownSearch>
            <DropdownSearch.Trigger
              contentData={selectedSite}
              isSingleItem={sitesFilteredByOrganization.length === 1}
              isLoading={(isLoading || !hasEverLoaded) && !selectedSite}
            />
            <DropdownSearch.Menu>
              <DropdownSearch.FilteredSearch
                listToFilter={sitesFilteredByOrganization}
                itemGetter={(site, closeMenu) => (
                  <StyledLinkDiv>
                    {site.data.name}
                    <PermissionsGate
                      requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                      {getEditLink(site, closeMenu)}
                    </PermissionsGate>
                  </StyledLinkDiv>
                )}
                itemSearchTerms={(site) => [
                  site.data.name,
                  site.data.name.replaceAll('.', ''),
                  site.data.name.replace(/[^A-Z]+/g, ''), // Capital letters only
                ]}
                onSelect={handleSiteSelect}
                placeholder='Search Sites(…)'
                extra={({ closeMenu }) => (
                  <PermissionsGate
                    requires={[PERMISSION_CANTILEVER_CORE_ADMIN]}>
                    {getAddButton(DrawerState.EditGeckoSite, closeMenu)}
                  </PermissionsGate>
                )}
              />
            </DropdownSearch.Menu>
          </DropdownSearch>
        </SelectorWrapper>
      )}
    </SiteSelectorsContainer>
  )
}
